import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HeroSection = ({ onCalculatorSelect }) => {
  return (
    <section className="relative bg-gradient-to-br from-primary via-primary/95 to-secondary/20 text-white overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-32 h-32 border border-white/20 rounded-full"></div>
        <div className="absolute top-40 right-32 w-24 h-24 border border-white/20 rounded-full"></div>
        <div className="absolute bottom-32 left-1/3 w-16 h-16 border border-white/20 rounded-full"></div>
        <div className="absolute bottom-20 right-20 w-20 h-20 border border-white/20 rounded-full"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="text-center max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-8">
            <Icon name="Calculator" size={16} color="white" />
            <span className="text-sm font-medium">Interactive ROI Tools</span>
            <div className="w-2 h-2 bg-secondary rounded-full animate-pulse"></div>
          </div>

          {/* Main Heading */}
          <h1 className="text-hero mb-6 leading-tight">
            Calculate Your
            <span className="block text-gradient-brand bg-gradient-to-r from-secondary to-accent bg-clip-text text-transparent">
              Data Strategy ROI
            </span>
          </h1>

          {/* Subheading */}
          <p className="text-xl lg:text-2xl text-white/90 mb-8 leading-relaxed max-w-3xl mx-auto">
            Quantify the financial impact of your data initiatives with our suite of interactive calculators. 
            Get personalized insights, cost projections, and implementation roadmaps.
          </p>

          {/* Key Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-4xl mx-auto">
            <div className="flex items-center space-x-3 bg-white/5 backdrop-blur-sm rounded-lg p-4">
              <div className="w-10 h-10 bg-secondary/20 rounded-lg flex items-center justify-center">
                <Icon name="TrendingUp" size={20} color="var(--color-secondary)" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-white">ROI Analysis</div>
                <div className="text-sm text-white/70">Quantify potential returns</div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 bg-white/5 backdrop-blur-sm rounded-lg p-4">
              <div className="w-10 h-10 bg-accent/20 rounded-lg flex items-center justify-center">
                <Icon name="Shield" size={20} color="var(--color-accent)" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-white">Risk Assessment</div>
                <div className="text-sm text-white/70">Evaluate compliance costs</div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 bg-white/5 backdrop-blur-sm rounded-lg p-4">
              <div className="w-10 h-10 bg-warning/20 rounded-lg flex items-center justify-center">
                <Icon name="Target" size={20} color="var(--color-warning)" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-white">Readiness Check</div>
                <div className="text-sm text-white/70">Assess current capabilities</div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <Button
              variant="default"
              size="lg"
              iconName="Calculator"
              iconPosition="left"
              onClick={() => onCalculatorSelect && onCalculatorSelect('data-strategy')}
              className="bg-secondary hover:bg-secondary/90 text-white border-secondary hover:border-secondary/90 hover-lift"
            >
              Start ROI Calculator
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="FileText"
              iconPosition="left"
              asChild
              className="border-white/30 text-white hover:bg-white/10 hover:border-white/50 hover-lift"
            >
              <Link to="/knowledge-hub-thought-leadership-center">
                View Sample Reports
              </Link>
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-8 text-white/70 text-sm">
            <div className="flex items-center space-x-2">
              <Icon name="Users" size={16} />
              <span>500+ Organizations Assessed</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={16} />
              <span>5-10 Minutes Per Calculator</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Download" size={16} />
              <span>Detailed PDF Reports</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1200 120" fill="none" className="w-full h-12 lg:h-16">
          <path
            d="M0,120 L0,60 Q300,0 600,60 T1200,60 L1200,120 Z"
            fill="var(--color-background)"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;